import fuzzySearch from 'frontcore/utils/helpers/fuzzySearch'
import latinize from 'latinize'
import { computed, ComputedRef, ref, Ref } from 'vue'
import { DropDownMenuItemProps } from 'frontcore/views/drop-down/domain/types'
import { levenshteinDistance } from 'frontcore/utils/helpers/levenshteinDistance'
import { Nullable } from 'frontcore/presentation/types'

interface Props {
  dataTest?: string
  extraItems: DropDownMenuItemProps[]
  iconName?: string
  items: DropDownMenuItemProps[]
  value: Nullable<DropDownMenuItemProps['id']> | DropDownMenuItemProps['id'][]
}

const useSelectSelection = (props: Props, multiple = false) => {
  const searchText: Ref<string | null> = ref(null)
  const emitSearchValue = (e:Event) => {
    const value = (e.target as HTMLInputElement).value
    searchText.value = value !== '' ? value : null
  }

  const dropDownItems: ComputedRef<DropDownMenuItemProps[]> = computed(() => {
    const itemIds = props.items.map((item) => item.id)
    const totalItems = props.items.concat(props.extraItems.filter(x => !itemIds.includes(x.id)))
    return buildDropDownItems(totalItems)
  })

  const filteredDropDownItems = computed(() => {
    let totalItems = buildDropDownItems(props.items)
    if (searchText.value !== null && props.extraItems.length > 0) {
      totalItems = dropDownItems.value
    }
    const fuzzyResults =  totalItems.filter(item => {
      if (searchText.value === null) { return true }
      let subGroupSearch = false
      if (item.group) {
        subGroupSearch = fuzzySearch(
          latinize(item.group.toLowerCase()),
          latinize(searchText.value.toLowerCase())
        )
      }
      return fuzzySearch(
        latinize(item.text.toLowerCase()),
        latinize(searchText.value.toLowerCase())
      ) || subGroupSearch
    })
    if (searchText.value === null) { return fuzzyResults }
    return sortByLevenshteinDistance(searchText.value.toLowerCase(), fuzzyResults)
  })

  const buildDropDownItems = (itemsArray: DropDownMenuItemProps[]): DropDownMenuItemProps[] => {
    return itemsArray.map(item => {
      const textWithoutSpaces = item.text.replaceAll(' ', '')
      const defaultIconValue = multiple ? '' : props.iconName
      return {
        ...item,
        dataTest: item.dataTest ?? `${props.dataTest}-item-${textWithoutSpaces}`,
        iconName: item.iconName ?? defaultIconValue,
      }
    })
  }
 
  const sortByLevenshteinDistance = (inputString: string, stringArray: DropDownMenuItemProps[]): DropDownMenuItemProps[] => {
    return stringArray.sort((a, b) => {
      const distanceA = levenshteinDistance(inputString, a.text.toLowerCase())
      const distanceB = levenshteinDistance(inputString, b.text.toLowerCase())
      return distanceA - distanceB
    })
  }

  const selectedSingleItem = computed(() => {
    if (multiple || props.value === null) return null
    return dropDownItems.value.find(item => props.value === item.id) ?? null
  })

  const selectedItem = (selectedItemId: Nullable<DropDownMenuItemProps['id']>): DropDownMenuItemProps | null => {
    return selectedItems.value.find(item => selectedItemId === item.id) ?? null
  }

  const selectedItems = computed((): DropDownMenuItemProps[] => {
    const inputArray = Array.isArray(props.value) ? props.value : []
    if (inputArray === null || !multiple) return []
    return dropDownItems.value.filter(item => inputArray.includes(item.id))
  })

  return {
    emitSearchValue,
    filteredDropDownItems,
    searchText,
    selectedSingleItem,
    selectedItem,
    selectedItems,
  }
}

export default useSelectSelection
